/**
 * 机位预设（P0-5）：14 个我方自标定的球面参数化预设。
 *
 * 与竞品的关系（计划书 §11.1 + 合规要求）：竞品实测只有 14 个预设定义、
 * 数值未逐一对抄——这里参数全部我方自定，语义覆盖"正面/侧面/过肩/俯仰"等
 * 短剧分镜常用景别。命名/参数是我们的，不镜像竞品表达。
 *
 * 参数化模型（简单可解释，标定=填数）：
 *   相机位于"瞄准点"球面上：aim = 角色脚底 + [0, aimFrac×身高, 0]
 *   距离 dist = distH×身高（身高按 TARGET_HEIGHT_M=1.75 米）
 *   yaw=方位角（0=角色+Z方向，即正前方），pitch=仰角（正=相机在上俯视）
 *   fov 走我方模型钳制范围。
 * 姿态跟随（角色转身预设跟着转）v1 不做——预设按世界方位角解算，
 * 角色朝向编辑属 P0-6 姿势层，记 D-30 备注。
 */
import { Vector3 } from 'three';

import type { Vec3 } from '../../model/director';

export const TARGET_HEIGHT_M = 1.75;

export interface CameraPreset {
  id: string;
  label: string;
  /** 方位角（度），0=+Z，顺时针俯视递增 */
  yawDeg: number;
  /** 仰角（度），正=相机高于瞄准点（俯视）；负=仰拍 */
  pitchDeg: number;
  /** 相机到瞄准点距离，单位=身高倍数 */
  distH: number;
  /** 视场角（度） */
  fov: number;
  /** 瞄准点高度，单位=身高比例（0.5=腰，0.92≈头） */
  aimFrac: number;
}

export const CAMERA_PRESETS: readonly CameraPreset[] = [
  { id: 'wide',      label: '大远景',   yawDeg: 0,   pitchDeg: 10,  distH: 6.0, fov: 35, aimFrac: 0.5 },
  { id: 'full',      label: '全景',     yawDeg: 0,   pitchDeg: 5,   distH: 4.2, fov: 40, aimFrac: 0.5 },
  { id: 'medium',    label: '中景',     yawDeg: 0,   pitchDeg: 8,   distH: 2.6, fov: 40, aimFrac: 0.62 },
  { id: 'closeup',   label: '特写',     yawDeg: 0,   pitchDeg: 5,   distH: 1.3, fov: 40, aimFrac: 0.92 },
  { id: 'bust',      label: '头肩',     yawDeg: 25,  pitchDeg: 8,   distH: 1.8, fov: 35, aimFrac: 0.88 },
  { id: 'side',      label: '侧面',     yawDeg: 90,  pitchDeg: 5,   distH: 3.4, fov: 40, aimFrac: 0.55 },
  { id: 'side-med',  label: '侧面中景', yawDeg: 90,  pitchDeg: 8,   distH: 2.4, fov: 42, aimFrac: 0.6 },
  { id: 'back',      label: '背面',     yawDeg: 180, pitchDeg: 8,   distH: 3.8, fov: 40, aimFrac: 0.55 },
  { id: 'low',       label: '仰拍',     yawDeg: 20,  pitchDeg: -35, distH: 2.6, fov: 50, aimFrac: 0.75 },
  { id: 'high',      label: '俯拍',     yawDeg: 30,  pitchDeg: 40,  distH: 4.2, fov: 40, aimFrac: 0.45 },
  { id: 'top',       label: '顶拍',     yawDeg: 0,   pitchDeg: 78,  distH: 3.6, fov: 45, aimFrac: 0.5 },
  { id: 'shoulderL', label: '左过肩',   yawDeg: 55,  pitchDeg: 6,   distH: 1.6, fov: 38, aimFrac: 0.9 },
  { id: 'shoulderR', label: '右过肩',   yawDeg: -55, pitchDeg: 6,   distH: 1.6, fov: 38, aimFrac: 0.9 },
  { id: 'oblique',   label: '斜侧低角', yawDeg: 45,  pitchDeg: -12, distH: 4.0, fov: 42, aimFrac: 0.5 },
];

export function findPreset(id: string): CameraPreset | null {
  return CAMERA_PRESETS.find((p) => p.id === id) ?? null;
}

/**
 * 新建机位命名：`基础名 + 两位序号`（取证：竞品"正面中景01"风格）。
 * 扫描已有名称里同前缀的最大序号 +1；纯函数可离线断言。
 */
export function nextCameraName(existing: readonly string[], base: string): string {
  let max = 0;
  for (const name of existing) {
    if (!name.startsWith(base)) {
      continue;
    }
    const suffix = name.slice(base.length);
    if (!/^\d+$/.test(suffix)) {
      continue; // 非纯数字尾巴（如"机位A01"）不吃
    }
    const tail = Number.parseInt(suffix, 10);
    if (Number.isFinite(tail) && tail > max) {
      max = tail;
    }
  }
  return `${base}${String(max + 1).padStart(2, '0')}`;
}

export interface PresetPose {
  position: Vec3;
  target: Vec3;
}

const DEG = Math.PI / 180;

/** 由预设 + 角色脚底世界坐标解算机位（纯函数，可离线断言）。
 * charYawDeg：参考系 yaw（度）。默认 0=世界方位（「场景」tab）；
 * 传角色朝向 yaw 即得「角色」tab 语义——预设绕的是角色当前朝向，
 * 角色转身后重新解算，机位永远在"角色的正面 45°"这类相对位（取证决策 3）。 */
export function resolvePresetPose(
  characterFeet: Vec3,
  preset: CameraPreset,
  charYawDeg: number = 0,
  heightM: number = TARGET_HEIGHT_M,
): PresetPose {
  const aim = new Vector3(
    characterFeet[0],
    characterFeet[1] + preset.aimFrac * heightM,
    characterFeet[2],
  );
  const r = preset.distH * heightM;
  const az = (charYawDeg + preset.yawDeg) * DEG;
  const el = preset.pitchDeg * DEG;
  const pos = new Vector3(
    aim.x + r * Math.cos(el) * Math.sin(az),
    aim.y + r * Math.sin(el),
    aim.z + r * Math.cos(el) * Math.cos(az),
  );
  return {
    position: [pos.x, pos.y, pos.z],
    target: [aim.x, aim.y, aim.z],
  };
}
